import { useCallback, useEffect, useRef, useState } from 'react';
import { toastError } from '@strivelabs/ui-core';
import { transcribe } from '@/services/DataService';
import { useDictationSettingsStore } from '@/store/dictationSettingsStore';
import { logger } from '@/lib/logger';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';

/** Hard cap on a single recording; auto-stops and transcribes at this point. */
export const MAX_RECORDING_MS = 90_000;

/** Number of amplitude samples kept for the waveform. */
const LEVELS_WINDOW = 48;

/** Interval between amplitude samples; keeps the waveform legible and cuts re-render churn. */
const SAMPLE_INTERVAL_MS = 90;

export type DictationStatus = 'idle' | 'recording' | 'transcribing';

interface UseDictationOptions {
  onInsert: (text: string) => void;
}

interface UseDictationResult {
  status: DictationStatus;
  elapsedMs: number;
  levels: number[];
  start: () => void;
  stop: () => void;
  cancel: () => void;
}

/**
 * Batch dictation: records mic audio, samples amplitude for a live waveform,
 * and on stop uploads the clip for transcription and hands the text to
 * `onInsert`. Cancelling or any failure drops the audio and returns to idle
 * without touching composer text.
 */
export const useDictation = ({ onInsert }: UseDictationOptions): UseDictationResult => {
  const deviceId = useDictationSettingsStore((state) => state.deviceId);
  const trackEvent = useTrack();
  const [status, setStatus] = useState<DictationStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sampleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const capRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const canceledRef = useRef(false);
  const isMountedRef = useRef(true);
  const startingRef = useRef(false);

  const teardown = useCallback(() => {
    if (sampleIntervalRef.current !== null) clearInterval(sampleIntervalRef.current);
    if (timerRef.current !== null) clearInterval(timerRef.current);
    if (capRef.current !== null) clearTimeout(capRef.current);
    sampleIntervalRef.current = null;
    timerRef.current = null;
    capRef.current = null;
    // Detach handlers before releasing tracks: stopping tracks makes the
    // browser auto-fire `stop`, which would otherwise re-enter `onstop`.
    if (recorderRef.current) {
      recorderRef.current.onstop = null;
      recorderRef.current.ondataavailable = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void audioContextRef.current?.close();
    audioContextRef.current = null;
    recorderRef.current = null;
    startingRef.current = false;
  }, []);

  const start = useCallback(async () => {
    if (status !== 'idle' || startingRef.current) return;
    startingRef.current = true;
    canceledRef.current = false;
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { ideal: deviceId } } : true,
      });
      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        startingRef.current = false;
        return;
      }
      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      audioContext.createMediaStreamSource(stream).connect(analyser);
      const buffer = new Uint8Array(analyser.frequencyBinCount);
      const sample = () => {
        analyser.getByteTimeDomainData(buffer);
        let peak = 0;
        for (const value of buffer) peak = Math.max(peak, Math.abs(value - 128) / 128);
        const level = Math.min(1, peak * 1.8);
        setLevels((prev) => [...prev, level].slice(-LEVELS_WINDOW));
      };
      sampleIntervalRef.current = setInterval(sample, SAMPLE_INTERVAL_MS);

      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        teardown();
        if (canceledRef.current) {
          if (isMountedRef.current) {
            setStatus('idle');
            setLevels([]);
          }
          return;
        }
        if (!isMountedRef.current) return;
        setStatus('transcribing');
        transcribe(blob)
          .then(({ text }) => {
            if (isMountedRef.current && text.trim()) onInsert(text.trim());
          })
          .catch((error) => {
            logger(error as Error, { context: 'useDictation: transcription failed' });
            if (isMountedRef.current) {
              toastError(error instanceof Error ? error.message : 'Could not transcribe audio');
            }
          })
          .finally(() => {
            if (isMountedRef.current) {
              setStatus('idle');
              setLevels([]);
              setElapsedMs(0);
            }
          });
      };

      recorder.start();
      setStatus('recording');
      setElapsedMs(0);
      const startedAt = Date.now();
      timerRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 200);
      capRef.current = setTimeout(() => recorderRef.current?.stop(), MAX_RECORDING_MS);
    } catch (error) {
      logger(error as Error, { context: 'useDictation: could not start recording' });
      toastError('Microphone unavailable. Check your browser permissions.');
      teardown();
      setStatus('idle');
    }
  }, [status, deviceId, onInsert, teardown]);

  const stop = useCallback(() => {
    if (status !== 'recording') return;
    // Only a completed recording counts — `cancel` discards the audio.
    trackEvent(ANALYTICS_EVENTS.CHAT_DICTATION_USED, {});
    recorderRef.current?.stop();
  }, [status, trackEvent]);

  const cancel = useCallback(() => {
    if (status !== 'recording') return;
    canceledRef.current = true;
    recorderRef.current?.stop();
  }, [status]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      teardown();
    },
    [teardown],
  );

  return { status, elapsedMs, levels, start, stop, cancel };
};
